/**
 * Small presentational pieces shared by the attendance screens (Stjórnun → Mæting and
 * Stillingar → Mætingin mín): bars, result pills and the per-event result list.
 */
import { CircleCheckBig, CircleMinus, CircleX, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import type { AttendanceResult, MemberEventAttendanceDTO } from '../../../../shared/dto/stats';
import { cx } from '../../../components/ui';
import { useI18n, type MessageKey } from '../../../i18n';
import { formatShortDay, formatTimeRange } from '../../../lib/dates';
import { KindIcon, OK_FILL, WARN_FILL } from '../../calendar/shared';
import { barWidth } from './model';

/** Horizontal bar (decorative — the number is always shown as text next to it). */
export function Bar({ pct, color, className }: { pct: number | null; color: string; className?: string }) {
  return (
    <div aria-hidden className={cx('h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)] ring-1 ring-[var(--line)] ring-inset', className)}>
      <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${barWidth(pct)}%`, background: color }} />
    </div>
  );
}

export const RESULT_KEY: Record<AttendanceResult, MessageKey> = {
  present: 'stats.present',
  excused: 'stats.excused',
  absent: 'stats.absent',
};

const RESULT_STYLE: Record<AttendanceResult, { icon: LucideIcon; pill: string }> = {
  present: { icon: CircleCheckBig, pill: `${OK_FILL} text-white` },
  excused: { icon: CircleMinus, pill: `${WARN_FILL} text-white` },
  absent: { icon: CircleX, pill: 'bg-[var(--danger)] text-white' },
};

export function ResultPill({ result }: { result: AttendanceResult }) {
  const { t } = useI18n();
  const s = RESULT_STYLE[result];
  return (
    <span className={cx('inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[15px] font-semibold', s.pill)}>
      <s.icon className="size-4.5" aria-hidden />
      {t(RESULT_KEY[result])}
    </span>
  );
}

/** "þri. 1. okt." + the year when it is not the current one. */
export function shortEventDate(ms: number, loc: string, nowMs = Date.now()): string {
  const day = formatShortDay(ms, loc);
  const y = new Date(ms).getUTCFullYear();
  return y === new Date(nowMs).getUTCFullYear() ? day : `${day} ${y}`;
}

/** Counted events of one member with their results, newest first. */
export function AttendanceEventList({ events }: { events: readonly MemberEventAttendanceDTO[] }) {
  const { t, loc } = useI18n();
  const rows = useMemo(() => [...events].sort((a, b) => b.startsAt - a.startsAt), [events]);
  if (rows.length === 0) return <p className="py-6 text-center text-[var(--muted)]">{t('stats.detail.empty')}</p>;
  return (
    <ul className="divide-y divide-[var(--line)]">
      {rows.map((e) => (
        <li key={e.eventId} className="flex min-h-16 items-center gap-3 py-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-soft)] text-[var(--accent)]">
            <KindIcon kind={e.kind} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{e.title}</div>
            <div className="text-[15px] text-[var(--muted)]">
              {shortEventDate(e.startsAt, loc)} · {t(`calendar.kind.${e.kind}`)} · {formatTimeRange(e.startsAt, e.endsAt, loc)}
            </div>
            {e.overridden ? <div className="text-[14px] text-[var(--ink-2)] italic">{t('stats.overridden')}</div> : null}
          </div>
          <ResultPill result={e.result} />
        </li>
      ))}
    </ul>
  );
}

/** Present / excused / absent counts as three labelled numbers. */
export function CountsRow({ present, excused, absent }: { present: number; excused: number; absent: number }) {
  const { t } = useI18n();
  const items: [MessageKey, number, string][] = [
    ['stats.present', present, 'var(--ok)'],
    ['stats.excused', excused, 'var(--warn)'],
    ['stats.absent', absent, 'var(--danger)'],
  ];
  return (
    <dl className="grid grid-cols-3 gap-2">
      {items.map(([k, n, color]) => (
        <div key={k} className="flex flex-col rounded-xl bg-[var(--surface-2)] px-3 py-2" style={{ borderLeft: `4px solid ${color}` }}>
          <dt className="text-[14px] font-semibold text-[var(--muted)]">{t(k)}</dt>
          <dd className="text-[22px] leading-tight font-bold tabular-nums">{n}</dd>
        </div>
      ))}
    </dl>
  );
}
